import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Crown, Users, ArrowRight, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { TrustBadge } from "@/components/TrustBadge";
import { useAuth } from "@/context/AuthContext";

export function TrustCard() {
  const navigate = useNavigate();
  const { user, refreshMe } = useAuth();
  const [request, setRequest] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/verification/mine").then((r) => setRequest(r.data.request)).catch(() => {}); }, []);

  const requestVerify = async () => {
    setBusy(true);
    try {
      await api.post("/verification/request", { note: "" });
      toast.success("Verification request submitted for review");
      const r = await api.get("/verification/mine"); setRequest(r.data.request);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const idVerified = user?.id_verified;
  const plan = user?.plan || "free";

  return (
    <div className="bg-white rounded-2xl border-2 border-border shadow-sm p-6 sm:p-8" data-testid="trust-card">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 rounded-xl bg-cream border-2 border-border flex items-center justify-center">
          <ShieldCheck size={26} strokeWidth={2.5} className="text-terracotta" />
        </div>
        <div>
          <h2 className="font-head text-2xl font-bold text-forest-900">Trust & verification</h2>
          <p className="text-forest-500">Trust is what makes Connect safe. Your level right now:</p>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap mb-5">
        <TrustBadge level={user?.trust_level || "unverified"} />
        <span className="text-forest-500">· {user?.completed_count || 0} completed · {user?.vouch_count || 0} vouches</span>
      </div>

      {!idVerified ? (
        <div className="rounded-xl bg-cream border-2 border-border p-5">
          <p className="font-bold text-forest-900 mb-1">Become ID Verified 🔵</p>
          <p className="text-forest-700 mb-4">Get a verified badge and priority matching. Ask two people who know you to vouch for you, request admin review, or upgrade to Connect Verified.</p>
          {request?.status === "pending" ? (
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold text-forest-900 font-bold"><Clock size={18} strokeWidth={2.5} /> Request pending review</span>
          ) : (
            <div className="flex flex-wrap gap-3">
              <button onClick={requestVerify} disabled={busy} data-testid="request-verification"
                className="min-h-[52px] px-5 rounded-full bg-forest-700 text-white font-bold hover:bg-forest-900 transition-colors disabled:opacity-60">
                {busy ? "Submitting…" : "Request verification"}
              </button>
              <button onClick={() => navigate("/app/plans")} data-testid="trust-upgrade"
                className="min-h-[52px] px-5 rounded-full bg-terracotta text-white font-bold hover:bg-terracotta-dark transition-colors flex items-center gap-2">
                Upgrade to Verified <ArrowRight size={18} strokeWidth={2.5} />
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="rounded-xl bg-forest-700 text-white p-5 flex items-center gap-3">
          <ShieldCheck size={26} strokeWidth={2.5} className="text-gold" />
          <p className="font-semibold">You're ID Verified — members can see you're trusted.</p>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-3 mt-4">
        <button onClick={() => navigate("/app/plans")} data-testid="profile-plans"
          className="min-h-[56px] rounded-xl border-2 border-border hover:border-forest-500 transition-colors flex items-center gap-3 px-5 text-left">
          <Crown size={24} strokeWidth={2.5} className="text-terracotta" />
          <span><span className="font-bold text-forest-900 block">Plans</span><span className="text-sm text-forest-500">You're on {plan === "free" ? "Basic" : plan === "family" ? "Family" : "Verified"}</span></span>
        </button>
        <button onClick={() => navigate("/app/family")} data-testid="profile-family"
          className="min-h-[56px] rounded-xl border-2 border-border hover:border-forest-500 transition-colors flex items-center gap-3 px-5 text-left">
          <Users size={24} strokeWidth={2.5} className="text-terracotta" />
          <span><span className="font-bold text-forest-900 block">Connect Family</span><span className="text-sm text-forest-500">Manage a loved one</span></span>
        </button>
      </div>
    </div>
  );
}
